import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { BlockEditor } from "./BlockEditor";
import type { ReportBlock } from "../api/types";
import { metricLabel } from "../utils/metricOptions";

const InsertDivider: React.FC<{ onInsert: (e: React.MouseEvent) => void }> = ({ onInsert }) => {
  const [hover, setHover] = useState(false);
  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        position: "relative",
        height: 20,
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        mx: -1,
        px: 1,
      }}
      onClick={onInsert}
    >
      <Box
        sx={{
          flex: 1,
          height: 2,
          bgcolor: hover ? "primary.main" : "divider",
          transition: "background-color 0.15s",
          borderRadius: 1,
          opacity: hover ? 1 : 0.3,
        }}
      />
      <Tooltip title="Insert block here" placement="top">
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: hover ? "primary.main" : "action.hover",
            border: "2px solid",
            borderColor: hover ? "primary.main" : "divider",
            borderRadius: "50%",
            display: "flex",
            color: hover ? "#fff" : "text.disabled",
            transition: "all 0.15s",
            zIndex: 10,
          }}
        >
          <AddCircleOutlineIcon fontSize="small" />
        </Box>
      </Tooltip>
    </Box>
  );
};

/** Short label for a data block — used as narrative generation context. */
const blockContextLabel = (b: ReportBlock): string | null => {
  switch (b.type) {
    case "chart":
      return b.title || metricLabel(b.metric);
    case "execSummary":
      return b.title || "Executive Summary";
    case "breakdown":
      return b.title || "Breakdown";
    case "kpiCards":
      return b.title || "Key Metrics";
    case "correlation":
      return b.title || "Correlation Matrix";
    case "scatter":
      return b.title || "Scatter";
    case "weatherImpact":
      return b.title || "Weather Impact";
    case "aiPanel":
      return b.title || "AI Wellness";
    default:
      return null;
  }
};

interface ReportCanvasProps {
  blocks: ReportBlock[];
  clientId: string | null;
  onChange: (blocks: ReportBlock[]) => void;
  /** Generates Director-targeted narrative; hint = preceding block context. */
  onGenerateNarrative?: (hint?: string) => Promise<string>;
  /** Generates an AI action plan; wired only to action-plan blocks. */
  onGenerateActionPlan?: () => Promise<string[]>;
  /** Called when user clicks the insert-between divider; receives insertion index + mouse event. */
  onInsertAt?: (index: number, e: React.MouseEvent) => void;
}

/** One sortable row: wires dnd-kit's transform + drag handle into BlockEditor. */
const SortableBlock: React.FC<{
  block: ReportBlock;
  clientId: string | null;
  onChange: (block: ReportBlock) => void;
  onRemove: () => void;
  onGenerateNarrative?: (hint?: string) => Promise<string>;
  onGenerateActionPlan?: () => Promise<string[]>;
  precedingBlock?: ReportBlock;
}> = ({
  block,
  clientId,
  onChange,
  onRemove,
  onGenerateNarrative,
  onGenerateActionPlan,
  precedingBlock,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });

  const narrativeHint = precedingBlock ? blockContextLabel(precedingBlock) ?? undefined : undefined;
  const generateNarrativeWithHint =
    block.type === "narrative" && onGenerateNarrative
      ? () => onGenerateNarrative(narrativeHint)
      : undefined;

  return (
    <Box
      ref={setNodeRef}
      sx={{ opacity: isDragging ? 0.5 : 1 }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <BlockEditor
        block={block}
        clientId={clientId}
        onChange={onChange}
        onRemove={onRemove}
        dragHandleProps={{ ...attributes, ...listeners }}
        onGenerateNarrative={generateNarrativeWithHint}
        onGenerateActionPlan={
          block.type === "actionPlan" ? onGenerateActionPlan : undefined
        }
      />
    </Box>
  );
};

/**
 * Drag-and-drop report canvas. Reorders blocks on drop and proxies per-block
 * edits/removals up to the parent, which owns the layout state.
 */
export const ReportCanvas: React.FC<ReportCanvasProps> = ({
  blocks,
  clientId,
  onChange,
  onGenerateNarrative,
  onGenerateActionPlan,
  onInsertAt,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(blocks, oldIndex, newIndex));
  };

  const updateBlock = (id: string, next: ReportBlock) =>
    onChange(blocks.map((b) => (b.id === id ? next : b)));

  const removeBlock = (id: string) =>
    onChange(blocks.filter((b) => b.id !== id));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={blocks.map((b) => b.id)}
        strategy={verticalListSortingStrategy}
      >
        {onInsertAt && blocks.length === 0 && (
          <InsertDivider onInsert={(e) => onInsertAt(0, e)} />
        )}
        {blocks.map((block, i) => (
          <React.Fragment key={block.id}>
            {i > 0 && onInsertAt && (
              <InsertDivider onInsert={(e) => onInsertAt(i, e)} />
            )}
            <SortableBlock
              block={block}
              clientId={clientId}
              onChange={(next) => updateBlock(block.id, next)}
              onRemove={() => removeBlock(block.id)}
              onGenerateNarrative={onGenerateNarrative}
              onGenerateActionPlan={onGenerateActionPlan}
              precedingBlock={i > 0 ? blocks[i - 1] : undefined}
            />
          </React.Fragment>
        ))}
        {onInsertAt && blocks.length > 0 && (
          <InsertDivider onInsert={(e) => onInsertAt(blocks.length, e)} />
        )}
      </SortableContext>
    </DndContext>
  );
};
